import {
  Box,
  Button,
  Field,
  Flex,
  Input,
  Link,
  Select,
  Text,
} from '@workwhile/ui'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useBookingDetailsContextValue } from '../context/BookingDetailsProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DayforceShiftAssociationTypes } from 'pages/Dayforce/constants'
import * as ROUTES from 'constants/routes'

const linkShiftFormSchema = z.object({
  shiftId: z.coerce
    .number({
      invalid_type_error: 'Shift ID must be a number',
    })
    .int()
    .min(1, 'Shift ID is required'),
  associationType: z.string(),
})

type LinkShiftFormValues = z.infer<typeof linkShiftFormSchema>

export const LinkShiftForm = () => {
  const {
    state: {
      dayforceBooking,
      linkShiftMutationState: { isPending, errorMessage },
    },
    actions: { linkShift },
  } = useBookingDetailsContextValue()

  const usedAssociationTypes = dayforceBooking.shifts.map(
    (shift) => shift.associationType
  )

  const associationTypeOptions = Object.entries(DayforceShiftAssociationTypes)
    .filter(([key]) => !usedAssociationTypes.includes(key))
    .map(([key, value]) => ({
      label: value,
      value: key,
    }))

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
  } = useForm<LinkShiftFormValues>({
    resolver: zodResolver(linkShiftFormSchema),
    defaultValues: {
      associationType: associationTypeOptions[0]?.value,
    },
  })

  if (!associationTypeOptions.length) {
    return null
  }

  const onSubmit = handleSubmit((data) =>
    linkShift(data.shiftId, data.associationType)
  )

  const bizPortalCreateShiftUrl = dayforceBooking.companyId
    ? `${ROUTES.COMPANY_SWITCH.replace(
        ':companyId',
        dayforceBooking.companyId.toString()
      )}?redirectUrl=${encodeURIComponent(
        `https://business.workwhilejobs.com/home/shift-editor/new`
      )}`
    : undefined

  return (
    <Flex flexDirection="column" gap={3}>
      <Flex flexDirection="row" gap={2} alignItems="center">
        {bizPortalCreateShiftUrl ? (
          <Button
            as={Link}
            to={bizPortalCreateShiftUrl}
            variant="secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Create shift in Biz portal
          </Button>
        ) : null}
        {dayforceBooking.suggestedShiftId ? (
          <Text size="small">
            Suggested Shift ID:{' '}
            <Link
              to={ROUTES.SHIFT_DETAIL.replace(
                ':shiftId',
                dayforceBooking.suggestedShiftId.toString()
              )}
              target="_blank"
            >
              {dayforceBooking.suggestedShiftId}
            </Link>
          </Text>
        ) : null}
      </Flex>
      <form onSubmit={onSubmit}>
        <Flex flexDirection="row" gap={2} flexWrap="wrap">
          <Field
            label="Shift ID"
            error={errors.shiftId?.message ?? errorMessage}
          >
            <Input type="number" {...register('shiftId')} min={1} />
          </Field>
          <Field
            label="Association Type"
            error={errors.associationType?.message}
          >
            <Controller
              control={control}
              {...register('associationType')}
              render={({ field, fieldState }) => (
                <Select
                  options={associationTypeOptions}
                  error={fieldState.error?.message}
                  value={associationTypeOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(option) => {
                    field.onChange(option)
                    setValue('associationType', option.value)
                  }}
                />
              )}
            />
          </Field>
        </Flex>
        <Box>
          <Button type="submit" isLoading={isPending} isDisabled={isPending}>
            Link Shift
          </Button>
        </Box>
      </form>
    </Flex>
  )
}
